<template>
    <section
        class="banner_container"
        v-if="props.quoteContent?.eyebrow || props.quoteContent?.headline"
        :style="backgroundColorStyle"
        id="quote_section"
    >
        <Media
            v-if="props.backgroundImage.file"
            :media="props.backgroundImage.file"
            :alt="props.backgroundImage.alt"
            :default-media-type="props.backgroundImage.type"
            mediaClasses="banner_container-back_image"
        />
        <div class="banner_container-modal">
            <div
                :class="
                    props.reversePositioning
                        ? `banner_container-content`
                        : `banner_container-content_reverse`
                "
            >
                <div :class="props.reversePositioning
                        ? `banner_container-content_image`
                        : `banner_container-content_image_reverse`
                ">
                    <Media
                        :media="props.media.file"
                        :default-media-type="props.media.type"
                        :alt="props.media.altText"
                        mediaClasses="banner_container-content_image-item1"
                    />
                </div>
                <div class="banner_container-text_container">
                    <div class="banner_container-text_content">
                        <img
                            v-if="hasEyebrowImage"
                            alt="eyebrow image"
                            class="inset_container-image_signature"
                            :src="textContent.eyebrowImage"
                        />
                        <p v-else :class="props.textContent?.eyebrowColor ? 
                                `banner_container-text_header eyebrow text-${props.textContent.eyebrowColor}` :
                                `banner_container-text_header eyebrow`">
                            {{ props.textContent.eyebrow }}
                        </p>
                        <h2 :class="props.textContent?.headerColor ? `headline-xl text-${props.textContent.headerColor}` : `headline-xl`">
                            {{ props.textContent.header }}
                        </h2>
                        <p :style="textAlignStyle" :class="props.textContent?.bodyColor ? `text-${props.textContent.bodyColor}` : ``">
                            {{ props.textContent.body }}
                        </p>
                        <div
                            class="banner_container-button"
                            v-if="props.textContent.cta"
                        >
                            <RothcoCta
                                v-if="props.textContent.cta.label"
                                :ctaType="props.textContent.cta.type"
                                :class="[
                                    ...getCtaClasses(props.textContent.cta)
                                ]"
                                v-bind="getCtaLink(props.textContent.cta)"
                                :aria-label="props.textContent.cta.aria_label"
                            >
                                {{ props.textContent.cta.label }}
                            </RothcoCta>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="banner_container-promo">
            <p :class="props.quoteContent?.eyebrowColor ? 
                `eyebrow-lg banner_container-promo_header text-${props.quoteContent?.eyebrowColor}` : 
                `eyebrow-lg banner_container-promo_header`">
                {{ props.quoteContent.eyebrow }}
            </p>
            <h2 :class="props.quoteContent?.headlineColor ? 
                `banner_container-promo_text headline-l text-${props.quoteContent?.headlineColor}` 
                : `banner_container-promo_text headline-l`">
                {{ props.quoteContent.headline }}
            </h2>
            <RothcoCta
                v-if="props.quoteContent?.cta?.label"
                :ctaType="props.quoteContent.cta.type"
                :class="[
                    'banner_container-promo_link',
                    ...getCtaClasses(props.quoteContent.cta)
                ]"
                v-bind="getCtaLink(props.textContent.cta)"
                :aria-label="props.quoteContent.cta.aria_label"
            >
                {{ props.quoteContent.cta.label }}
            </RothcoCta>
            <div v-else class="banner_container-promo_link"></div>
        </div>
    </section>
    <div v-else>
        <section class="inset_container" id="inset_section">
            <Media
                v-if="props.backgroundImage.file"
                :media="props.backgroundImage.file"
                :alt="props.backgroundImage.alt"
                :default-media-type="props.backgroundImage.type"
                mediaClasses="inset_container-back_image"
            />
            <div class="inset_container-modal">
                <div
                    :class="
                        props.reversePositioning
                            ? `inset_container-modal_content_reverse`
                            : `inset_container-modal_content`
                    "
                >
                    <div class="inset_container-image_two">
                        <Media
                            :media="props.media.file"
                            :default-media-type="props.media.type"
                            :alt="props.media.altText"
                            media-classes="inset_container-image"
                        />
                    </div>
                    <div class="inset_container-text_content">
                        <img
                            v-if="hasEyebrowImage"
                            alt="eyebrow image"
                            class="inset_container-image_signature"
                            :src="textContent.eyebrowImage"
                        />
                        <p v-else :class="props.textContent?.eyebrowColor ? 
                            `banner_container-text_header text-${props.textContent.eyebrowColor}` : 
                            `banner_container-text_header`">
                            {{ props.textContent.eyebrow }}
                        </p>
                        <h2 role="heading" aria-level="1" :class=" props.textContent?.headerColor ? 
                            `inset_container-title headline-xl text-${props.textContent.headerColor}` : 
                            `inset_container-title headline-xl`">
                            {{ props.textContent.header }}
                        </h2>
                        <p
                            :class="props.textContent?.bodyColor ? 
                                `inset_container-subtitle text-${props.textContent.bodyColor}` : 
                                `inset_container-subtitle`"
                            :style="textAlignStyle"
                        >
                            {{ props.textContent.body }}
                        </p>
                        <div
                            class="inset_container-button_container"
                            v-if="props.textContent.cta"
                        >
                            <RothcoCta
                                v-if="props.textContent.cta.label"
                                :ctaType="props.textContent.cta.type"
                                :class="[
                                    ...getCtaClasses(props.textContent.cta)
                                ]"
                                v-bind="getCtaLink(props.textContent.cta)"
                                :aria-label="props.textContent.cta.aria_label"
                            >
                                {{ props.textContent.cta.label }}
                            </RothcoCta>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
<script setup lang="ts">
    import { computed } from "vue";
    import Media from "@/components/Media.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import { type HeroTextInterface } from "@interfaces/HeroText";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    let props = defineProps({
        textContent: {
            type: Object as PropType<HeroTextInterface>,
            required: true,
            default: ()=>{
                return {};
            }
        },
        media: {
            type: Object,
            default() {
                return { type: "image", file: null, altText: null };
            },
            required: true
        },
        backgroundImage: {
            type: Object,
            default() {
                return { type: "image", file: null, alt: "background_image" };
            }
        },
        reversePositioning: {
            type: Boolean,
            default: false
        },
        quoteContent: {
            type: Object,
            default() {
                return {};
            }
        },
        backgroundColor: {
            type: String,
            default: ""
        }
    });

    const textAlignStyle = computed(() => {
        return {
            textAlign: props.textContent.textAlignment
        };
    });

    const backgroundColorStyle = computed(() => {
        return {
            backgroundColor: props.backgroundColor
        };
    });

    const hasEyebrowImage = computed(() => {
        return props.textContent.eyebrowImage?.length > 0;
    });

    const getCtaClasses = (cta) => {
        let classes = [cta.style ? cta.style : "primary"];
        if (cta.size != "default") classes.push(cta.size);
        return classes;
    };
</script>
